// <reference path='aEvents.ts' />
///<reference path="../../../node_modules/@types/geojson/index.d.ts"/>

/**
 * My personal abstract class for events
 */
class aEvents {

    /**
     * The event collection
     */
    protected events = {};

    /**
     * Add a function to run to a named event
     * @param event   Name of an event
     * @param handler A function to be called when event occurrs. First argument of this function is
     *                the object that triggers it, the second argument is the event name.
     */
    public on(event:string, handler) {
        if (!this.events.hasOwnProperty(event)) {
            this.events[event] = [];
        }
        this.events[event].push(handler);
    }

    /**
     * Delete all events of given name
     * @param event
     */
    public off(event:string) {
        if (this.events.hasOwnProperty(event)) {
            delete this.events[event];
        }
    }

    /**
     * Trigger the event
     * @param {string} event
     */
    protected triggerEvent(event:string) {
        if (this.events.hasOwnProperty(event)) {
            for (let handler of this.events[event]) {
                handler(this, event);
            }
        }
    }
}


/**
 * Controls the GUI for the position functionality in issue
 *
 * This map uses Leaflet library for maps:
 * https://leafletjs.com/
 *
 */
class IssuePositionGUI extends aEvents {

    /**
     * The map in the popup to select position
     */
    private map;

    private marker;


    /**
     * If default position is specified in constructor, this variable will contain the
     * default position as MyPositioin object.
     */
    private readonly defaultPosition:MyPosition;

    private readonly defaultZoomValue:number = 18;

    /**
     * The previous position gotten from the Geolocation API.
     * Null if not queried before.
     */
    private previousPositionFromGPS:MyPosition;

    /**
     * The URL of the map provider
     */
    private url;

    /**
     * Selectors throughout the DOM used in this class
     */
    private selectors = {
        lat: "#Sak_lat",
        lng: "#Sak_lng",
        zoom: "#Sak_zoom",
        button_remove_position: "#button-remove-position",
        button_update_address: "#button-update-address",
        button_view_map: "#button-view-map",
        map: "#map-position",
    };

    /**
     * Attribution to the map
     */
    private attribution = { attribution: "&copy; <a href=\"https://www.kartverket.no/\">Kartverket</a>" };


    /**
     * Constructor
     *
     * @param {string} hostname             The host name of the map service provider
     * @param {string} path                 The path to the map service with placeholders for zoom, x and y ("{z}", "{x}" and "{y}")
     * @param {Object} defaultPosition      The default position (used when user does not have GPS)
     * @param {number} defaultPosition.lat  Latitude
     * @param {number} defaultPosition.lng  Longitude
     */
    constructor(hostname:string, path:string, defaultPosition) {
        super();
        this.defaultPosition = new MyPosition(defaultPosition.lat, defaultPosition.lng);
        let protocol = location.protocol;
        this.url = protocol+"//"+hostname+path;
    }

    /**
     * Set new selectors
     * @param selectors
     */
    public setSelectors(selectors) {
        this.selectors = selectors;
    }


    /**
     * Om vi har en posisjon i GUI
     * @return {boolean}
     */
    public hasPosition() {
        let lat = parseFloat($(this.selectors.lat).val().toString());
        let lng = parseFloat($(this.selectors.lng).val().toString());
        return (!isNaN(lat) && !isNaN(lng));
    }

    /**
     * Om vi har allerede hentet GPS-posisjon
     */
    public hasGpsPosition():boolean {
        return typeof this.previousPositionFromGPS !== 'undefined';
    }

    // /**
    //  * Is device touch device?
    //  */
    // private isTouchDevice():boolean {
    //     return "ontouchstart" in window || Boolean(Number(navigator.msMaxTouchPoints));
    // }

    /**
     * Get selected position
     * @return {MyPosition}
     */
    public getPosition() {
        if (this.hasPosition()) {
            let lat = $(this.selectors.lat).val().toString();
            let lng = $(this.selectors.lng).val().toString();
            return new MyPosition(parseFloat(lat), parseFloat(lng));
        }
        return null;
    }

    /**
     * Handles states of elements
     */
    public handleState() {
        if (this.hasPosition()) {
            $(this.selectors.button_remove_position).show();
            $(this.selectors.button_update_address).show();
            $(this.selectors.button_view_map).html("Vis/oppdater posisjon");
        }
        else {
            $(this.selectors.map).hide();
            $(this.selectors.button_remove_position).hide();
            $(this.selectors.button_remove_position).hide();
            $(this.selectors.button_update_address).hide();
            $(this.selectors.button_view_map).html("Legg til posisjon");
        }
    }


    /**
     * Get GPS position.
     *
     * @param {MyPosition} defaultPosition Used when position cannot be detected
     * @param {function} successCallback A function that is called on success with position as argument
     * @param {function} errorCallback   A function that is called on error with position as argument
     */
    private getGpsPosition(defaultPosition:MyPosition, successCallback:(pos:MyPosition)=>void, errorCallback:(pos:MyPosition)=>void):void {
        if ("geolocation" in navigator) {
            if (location.protocol !== 'https:') {
                console.log("You are not on an encrypted site, not turning on geolocation");
                successCallback(defaultPosition);
            }
            else {
                // https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions
                let positionOptions = {
                    // defaults to false, but we need to know where the user is exactly
                    enableHighAccuracy: true,
                    // defaults to 0 which means cached values are valid. But we need a
                    // new position each time we ask for it. Value in milliseconds.
                    //maximumAge: 5*60*1000 // 5 minutes
                    maximumAge: 10
                };
                let self = this; /** @type {IssuePositionGUI} */
                navigator.geolocation.getCurrentPosition(
                    function(pos:GeolocationPosition) {
                        self.previousPositionFromGPS = new MyPosition(pos.coords.latitude, pos.coords.longitude);
                        successCallback(self.previousPositionFromGPS);
                    },
                    function(pos:GeolocationPositionError) {
                        alert(pos.message);
                        //let mypos = new MyPosition(pos.coords.latitude, pos.coords.longitude);
                        //errorCallback(mypos);
                        //throw new Error("Failed retrieving GPS position");
                    },
                    positionOptions
                );
            }
        }
        else {
            successCallback(defaultPosition);
        }
    }


    /**
     * Set new position for GPS to map
     * @param {boolean} reQuery Query the GPS again even if we previously queried the position from it
     */
    public setPositionFromGps(allowReQuery:boolean=true) {
        if (allowReQuery) {
            this.getGpsPosition(this.defaultPosition, (function (position) {
                    this.updatePosition(position);
                    this.map.setView(position);
                    //this.setPositionStaticMap(position);
                }).bind(this), // specify what is this in function
                function (position) {
                    //console.log("Could not get GPS position. Error msg: "+position);
                }
            );
        }
        // do not allow re-query of GPS position
        else {
            let pos = this.hasGpsPosition() ? this.previousPositionFromGPS : this.defaultPosition;

            if (pos != null) {
                this.updatePosition(pos);
                this.map.setView(pos);
            }
            else {
                console.log("Position cannot be set to map, because default position is not set and GPS position cannot be queried");
            }
        }
    }

    /**
     * Updates the position on the map.
     * This function expects the map to be initialized.
     */
    public updatePosition(pos:MyPosition) {
        $(this.selectors.map).show();
        $(this.selectors.lat).val(pos.getLat());
        $(this.selectors.lng).val(pos.getLng());
        this.updatePositionLabel();

        // Set marker on the map
        if (this.marker != undefined) { this.marker.remove(); }
        this.marker = L.marker(pos.getObject(), {}).addTo(this.map);
        // called because container might have been resized
        // https://github.com/Asymmetrik/ngx-leaflet/issues/105
        this.map.invalidateSize();

        // Handle the state if changed
        this.handleState();
        this.triggerEvent("updatePosition");
    }

    private updatePositionLabel() {
        if (this.hasPosition()) {
            let pos = this.getPosition();
            $("#position").text("Posisjon: "+pos.getLat()+", "+pos.getLng());
        }
        else {
            $("#position").text("Posisjon: (ingen)");
        }
    }

    /**
     * Update the label with address
     * @param {string} address
     */
    public updateAddressLabel() {
        let address = $("#Sak_adresse").val();
        let zip = $("#Sak_postnummer").val();
        let city = $("#Sak_poststed").val();
        let addressContent = null;
        if (address!="" && zip!="" && city!="") {
            addressContent = "Adresse: "+address+", "+zip+" "+city;
        }
        else {
            addressContent = "Adresse: (ingen)";
        }

        $("#address").text(addressContent);
    }

    /**
     * If map has initialized
     */
    public hasInitialized():boolean {
        return this.map === undefined;
    }

    /**
     * Initialize/Reinitialize the map
     */
    public initMap() {
        // If map is not initialized, then initlialize it
        if (this.map === undefined) {
            console.log("Initialized the map");
            let mapId = this.selectors.map.substr(1);
            this.map = L.map(mapId, {
                doubleClickZoom: true,
            });
            L.tileLayer(this.url, this.attribution).addTo(this.map);

            // Get zoom level changes
            this.map.on("zoom", function(event) {
                this.setZoom(this.map.getZoom())
                // position is null when no position is selected, and in that case we do not
                // update position to static map
                let pos = this.getPosition();
            }, this);

            // // Event to detect long tap/click and trigger setting new position on non-touch devices
            // this.map.on("mousedown", function(event) {
            //     let params = {
            //         start: new Date().getTime(),
            //         longpress: 500, // 0,5 sec
            //         map: this.map,
            //         marker: this.marker,
            //         obj: this
            //     };
            //
            //     // Here we detect the mouse up event, and calculate if we have a longpress
            //     params.map.once("mouseup", function(event) {
            //         if (new Date().getTime() >= (this.start + this.longpress)) {
            //             if (this.marker!=undefined) { this.marker.remove(); }
            //             let pos = new MyPosition(event.latlng.lat, event.latlng.lng);
            //             this.obj.updatePosition(pos);
            //             this.obj.setPositionStaticMap(pos);
            //         }
            //     }, params);
            // }, this);

            // This event (contextmenu) is dedicated to longtap (1 sec) according to leafletjs documentation:
            // https://leafletjs.com/reference-1.4.0.html#map-interaction-events
            // Usage of the contextmenu event:
            // https://stackoverflow.com/questions/23347977/leaflet-taphold-to-set-markers

            // Change event to tap/click instead of longtap (contextmenu)
            this.map.on("tap click", function (event) {
                if (this.marker != undefined) { this.marker.remove(); }
                let pos = new MyPosition(event.latlng.lat, event.latlng.lng);
                this.updatePosition(pos);
            }, this);

            // Set any position now when we are initializing the map
            let position = this.hasPosition() ? this.getPosition() : this.defaultPosition;
            this.map.setView(position, this.getZoom());
            if (this.marker != undefined) { this.marker.remove(); }
            this.marker = L.marker(position.getObject(), {}).addTo(this.map);
        }
    }

    /**
     * Get the map object
     */
    public getMapObject() {
        return this.map;
    }

    /**
     * Initialize the position on the map.
     * This code used to be in the iniMap() function, but we don't want querying the GPS position before we
     * actually need it.
     */
    public initPosition() {
        if (this.hasPosition()) {
            let position = this.getPosition();
            this.map.setView(position, this.getZoom());
            if (this.marker != undefined) { this.marker.remove(); }
            this.marker = L.marker(position.getObject(), {}).addTo(this.map);
        }
        else {
            this.getGpsPosition(this.defaultPosition, (function (position: MyPosition): void {
                    this.map.setView(position.getObject(), this.getZoom());
                    if (this.marker != undefined) { this.marker.remove(); }
                    this.marker = L.marker(position.getObject(), {}).addTo(this.map);
                    // called because container might have been resized
                    // https://github.com/Asymmetrik/ngx-leaflet/issues/105
                    this.map.invalidateSize();
                }).bind(this), // setting bind to set this to IssuePositionGUI
                function (position): void {
                    console.log("Could not get a GPS position");
                }
            );
        }
    }



    /**
     * Center map on the selected position with selected zoom level.
     * (does not change zoom level)
     */
    public setFocus() {
        if (this.hasPosition()) {
            this.map.setView(this.getPosition(), this.getZoom());
        }
    }


    private removePosition() {
        $(this.selectors.lat).val("");
        $(this.selectors.lng).val("");
        this.handleState();
        this.updateAddressLabel();
        this.updatePositionLabel();
        this.triggerEvent("removePosition");
    }

    /**
     * Repaint the map?
     */
    public repaintMap() {
        // called because container might have been resized
        // https://github.com/Asymmetrik/ngx-leaflet/issues/105
        this.map.invalidateSize();
    }


    /**
     * Set the zoom value
     * @param value This value has to be from 1 to 20, or an error is thrown
     */
    private setZoom(value:number):void {
        let zoom = parseInt(value.toString());
        if (zoom<=20 && zoom>=1) {
            $(this.selectors.zoom).val(zoom);
        }
        else {
            throw new Error("Invalid value for zoom: "+zoom);
        }
    }

    /**
     * Get the zoom level.
     * If not set, then default value is returned.
     * This is a bug:
     * @return {number}
     */
    private getZoom():number {
        let zoom = parseInt($(this.selectors.zoom).val().toString());
        if (isNaN(zoom)) {
            zoom = this.defaultZoomValue;
        }
        else if (zoom<1 || zoom>20) {
            zoom = this.defaultZoomValue;
        }
        return zoom;
    }


}


/**
 * A simple static map that cannot be scrolled, zoomed etc.
 *
 */
class ElaSimpleMap {

    /**
     * The url to the map service. Set from constructor.
     */
    private readonly url:string;

    /**
     * The map instance variable
     */
    private map;

    /**
     * The position sat
     */
    private position:MyPosition;

    /**
     * The configuration object
     */
    private config:{hostname:string, path:string, lat?:number, lng?:number, zoom?:number, selector:string}

    private attribution = { attribution: "&copy; <a href=\"https://www.kartverket.no/\">Kartverket</a>" };

    /**
     * The map marker.
     * This simple map has only one marker supported by the class. Added and updated
     * in the function setPosition(). You can add your own marker manually.
     */
    private marker;

    /**
     * The options for the map
     */
    private mapOptions = {
        attributionControl: true,
        zoomControl: false,
        doubleClickZoom: false,
        keyboard: false,
        //mouseWheelZoom: false,
        zoomAnimation: false,
        boxZoom: false,
        dragging: false,
        scrollWheelZoom: false,
        tap: false,
    };

    /**
     * Set up the map.
     *
     * @param {Object} config
     * @param {number} config.lat       Latitude  Latitude for position to be marked in the map as current position
     * @param {number} config.lng       Longitude Longitude for position to be marked in the map as current position
     * @param {string} config.hostname  Host name
     * @param {string} config.path      The path to add to the hostname
     * @param {number} config.zoom      Optional value for zoom
     * @param {string} config.selector  The selector for the map container
     * @param {Object} config.options   The options object for initializing the map object. If not given, then
     *                                  default values are used. You can add the load event by setting a function to the
     *                                  parameter "onLoad" here.
     */
    constructor(config:{hostname:string, path:string, selector:string, lat?:number, lng?:number, zoom?:number, options?:any}) {
        // make sure all required properties are given
        let requiredAttributes = ["hostname","path","selector"];
        for (let property of requiredAttributes) {
            if (!config.hasOwnProperty(property)) {
                throw new Error("Configuration does not contain the required property '"+property+"'");
            }
        }

        if (config.hasOwnProperty("options")) {
            this.mapOptions = config.options;
        }

        // Set default values if optional properties are not set
        if (!config.hasOwnProperty("zoom")) { config["zoom"] = 18; }

        this.config = config;

        let protocol = location.protocol; // http or https
        this.url = protocol+"//"+config.hostname+config.path;

        this.init();

        // If option "onLoad" is specified, then set the load event handler
        if (config.hasOwnProperty("options") && config.options.hasOwnProperty("onLoad")) {
            this.map.on("load", config.options.onLoad);
        }

        // if position is given, then set position in the map
        if (config.hasOwnProperty("lat") && config.hasOwnProperty("lng")) {
            this.setPosition({lat: config.lat, lng: config.lng, zoom: config.zoom });
        }
    }

    /**
     * Initialize the map
     */
    private init() {
        this.map = L.map(this.config.selector, this.mapOptions);
        L.tileLayer(this.url, this.attribution).addTo(this.map);
        this.map.attributionControl.setPrefix(false);
    }


    /**
     * Get the leaflet JS map object
     */
    public getMapObject() {
        return this.map;
    }

    /**
     * Remove the position and hide map
     */
    public removePosition() {
        this.position = null;
        $("#"+this.config.selector).hide();
    }


    /**
     * If map has initialized
     */
    public hasInitialized():boolean {
        return this.map === undefined;
    }


    /**
     * Set the position and zoom level for the map and show it on the map.
     * This function will add default marker to the given position. If you
     * need multiple markers, and/or different markers, then you need to add
     * these manually, and not through this function.
     */
    public setPosition(position:{lat:number, lng:number, zoom:number}) {
        $("#"+this.config.selector).show();
        this.position = new MyPosition(position.lat, position.lng);
        this.map.setView(this.position.getObject(), position.zoom);

        // Add marker to map
        if (this.marker != undefined) { this.marker.remove(); }
        this.marker = L.marker(position).addTo(this.map);
        this.map.invalidateSize();
    }


    /**
     * @return {boolean}
     */
    public hasPosition() {
        return typeof this.position === "undefined";
    }

    /**
     *
     */
    public repaintMap() {
        this.map.invalidateSize();
    }

}


/**
 * A class to represent a GPS position
 */
class MyPosition {

    private readonly lat;
    private readonly lng;

    constructor(lat:number, lng:number) {
        // validate the value
        let error = [];
        if (isNaN(lat)||lat===undefined) { error.push("Given latitude is not a number: "+lat); }
        if (isNaN(lng)||lng===undefined) { error.push("Given longitude is not a number: "+lng); }
        if (lng < -180 || lng > 180) { error.push("Longitude does not have a valid value: "+lng); }
        if (lat < -90 || lat > 90) { error.push("Latitude does not have a valid value: "+lat); }
        if (error.length>0) {
            throw new Error("Error messages when creating position:\n"+error.join("\n"));
        }
        else {
            this.lat = lat;
            this.lng = lng;
        }
    }

    public getLat(){
        return this.lat;
    }

    public getLng() {
        return this.lng;
    }

    /**
     * Get object latitude and longitude coordinates
     */
    public getObject():{lat:number, lng:number} {
        return {
            lat: this.lat,
            lng: this.lng
        };
    }

    /**
     * Get current users GPS position
     * @param callback - The callback when getting users GPS position is successful or we return the default position
     * @param {boolean} highAccuracy If API should call for high accuracy for position. Defaults to true.
     */
    static getGpsPosition(callback:(pos:MyPosition)=>void, highAccuracy=true) {
        // The default position to return when an error happens
        let defaultPosition = new MyPosition(62.718030, 7.169312);

        if ("geolocation" in navigator) {
            // If current web browser location is at a site without HTTPS, then error message
            if (location.protocol !== 'https:') {
                console.log("You are not on an encrypted site, not turning on geolocation");
                callback(defaultPosition);
            }
            else {
                // https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions
                let positionOptions = {
                    // defaults to false, but we need to know where the user is exactly
                    enableHighAccuracy: highAccuracy,
                    // defaults to 0 which means cached values are valid. But we need a
                    // new position each time we ask for it. Value in milliseconds.
                    //maximumAge: 5*60*1000 // 5 minutes
                    maximumAge: 10
                };
                navigator.geolocation.getCurrentPosition(
                    function(pos:GeolocationPosition) {
                        //let self = this; /** @type {MyPosition} */
                        callback(new MyPosition(pos.coords.latitude, pos.coords.longitude));
                    },
                    function(pos:GeolocationPositionError) {
                        alert(pos.message);
                        callback(defaultPosition);
                    },
                    positionOptions
                );
            }
        }
        else {
            callback(defaultPosition);
        }
    }

}